import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/react-hooks'
import { POSTSLM } from '@features/skillsTutor/api'

interface SkillsTutorLearningPath {
  AreAllUnitsCompleted?: boolean
  IsSkillTutorContent?: boolean
  SkillsTutorCompleted?: boolean
  RecordId?: string
}

interface UseSkillsTutorAutoCompletionParams {
  learningPaths?: {
    LearningPaths?: SkillsTutorLearningPath[]
  }
  refresh?: () => void
  isRefreshing?: boolean
}

interface SkillsTutorAutoCompletionState {
  isUpdating: boolean
  hasError: boolean
}

/**
 * Watches SkillsTutor learning paths and automatically posts completion
 * when all units are done but the SkillsTutor course is not yet marked completed.
 */
export const useSkillsTutorAutoCompletion = ({
  learningPaths,
  refresh,
  isRefreshing,
}: UseSkillsTutorAutoCompletionParams): SkillsTutorAutoCompletionState => {
  const [hasError, setHasError] = useState(false)
  const [postSLM, { loading: isUpdating }] = useMutation(POSTSLM, {
    onError: () => {
      setHasError(true)
    },
    onCompleted: () => {
      // Refresh the skills tutor data after successful completion update
      refresh?.()
    },
  })

  useEffect(() => {
    let isMounted = true

    if (isRefreshing) {
      return
    }

    const completedLearningPath = learningPaths?.LearningPaths?.find(
      (lp) => lp?.AreAllUnitsCompleted && lp?.IsSkillTutorContent && !lp?.SkillsTutorCompleted
    )

    if (!completedLearningPath) {
      return
    }

    if (isMounted) {
      postSLM({
        variables: {
          postSLMInput: {
            recordId: completedLearningPath?.RecordId || '',
            skillsTutorCompleted: true,
          },
        },
      })
    }

    return () => {
      isMounted = false
    }
  }, [learningPaths, postSLM, isRefreshing])

  return {
    isUpdating,
    hasError,
  }
}
